import { TripLeg, TripStop } from '../types/trip';

const EARTH_RADIUS_KM = 6371;
const DEFAULT_SPEED_KMH = 70;

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

export function haversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

export function estimateDriveMinutes(distanceKm?: number): number | undefined {
  if (distanceKm === undefined || Number.isNaN(distanceKm)) return undefined;
  return Math.round((distanceKm / DEFAULT_SPEED_KMH) * 60);
}

export function buildTripLegs(stops: TripStop[]): TripLeg[] {
  const legs: TripLeg[] = [];
  for (let i = 1; i < stops.length; i += 1) {
    const prev = stops[i - 1];
    const current = stops[i];
    let distanceKm: number | undefined;
    if (
      typeof prev.lat === 'number' &&
      typeof prev.lng === 'number' &&
      typeof current.lat === 'number' &&
      typeof current.lng === 'number'
    ) {
      distanceKm = Number(
        haversineDistanceKm(prev.lat, prev.lng, current.lat, current.lng).toFixed(1)
      );
    }
    const driveMinutes = estimateDriveMinutes(distanceKm);
    legs.push({
      fromId: prev.id,
      toId: current.id,
      distanceKm,
      driveMinutes,
    });
  }
  return legs;
}

export function summariseLegs(legs: TripLeg[]): {
  totalDistanceKm?: number;
  totalDriveMinutes?: number;
} {
  const totals = legs.reduce(
    (acc, leg) => {
      if (typeof leg.distanceKm === 'number') {
        if (typeof acc.d === 'number') {
          acc.d += leg.distanceKm;
        }
      } else {
        acc.d = undefined;
      }

      if (typeof leg.driveMinutes === 'number') {
        if (typeof acc.m === 'number') {
          acc.m += leg.driveMinutes;
        }
      } else {
        acc.m = undefined;
      }

      return acc;
    },
    { d: 0 as number | undefined, m: 0 as number | undefined }
  );

  return {
    totalDistanceKm:
      typeof totals.d === 'number' ? Number(totals.d.toFixed(1)) : undefined,
    totalDriveMinutes:
      typeof totals.m === 'number' ? Math.round(totals.m) : undefined,
  };
}

